// Pure, locale-aware currency formatter (contract 04 §3.2, §5, §16). Takes only an ISO-4217 code —
// the symbol and its placement come from `Intl.NumberFormat({ style: 'currency' })` per locale, so
// the legacy hand-maintained 82-entry `currencySymbols` map (locale-blind: one `'$'` for USD, CAD,
// AUD…) is gone. `formatCurrency(9.9, 'en-US', { currency: 'EUR' })` → `"€9.90"`.

import {
  SENTINEL,
  commonNumberOptions,
  getNumberFormat,
  guard,
  resolveFraction,
} from './createFormatter'
import type { FormatCurrencyOptions } from './types'

/**
 * Fallback currency used only when a caller passes no code and no default is bound. Real callers
 * inject the workspace / locale currency through `useFormat`; this keeps the pure function total.
 */
const FALLBACK_CURRENCY = 'USD'

/** Currency glyph only, for compatibility surfaces that expose a suffix separately from a cell. */
export function resolveCurrencySymbol(currency: string | undefined, locale: string): string {
  try {
    return (
      getNumberFormat(locale, {
        style: 'currency',
        currency: currency || FALLBACK_CURRENCY,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ??
      currency ??
      ''
    )
  } catch {
    return currency ?? ''
  }
}

/**
 * Format a monetary value. With no `minFrac`/`maxFrac`, Intl uses each currency's natural precision
 * (EUR/USD → 2, JPY → 0). An unknown currency code degrades to a plain number rather than throwing.
 */
export function formatCurrency(
  value: number | null | undefined,
  locale: string,
  options: FormatCurrencyOptions = {},
): string {
  if (guard(value)) return SENTINEL

  return getNumberFormat(locale, {
    style: 'currency',
    currency: options.currency ?? FALLBACK_CURRENCY,
    ...commonNumberOptions(options),
    ...resolveFraction(options),
  }).format(value)
}
